import * as React from "react";
import { Check, ChevronsUpDown, Loader2, Plus, Settings } from "lucide-react";
import { cn } from "@repo/utils";
import {
  Button,
  useToast,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@repo/ui";
import type { Patient } from "@/lib/types/patients";
import { getPatients } from "@/lib/api/patients";
import { ApiClientError } from "@/lib/api/client";

function formatPatientName(patient: Patient): string {
  const parts = [patient.first_name];
  if (patient.last_name) parts.push(patient.last_name);
  return parts.join(" ");
}

function formatUsageSummary(patient: Patient): string | null {
  const parts: string[] = [];
  if (patient.dispense_count > 0) {
    parts.push(
      `${patient.dispense_count} dispense${patient.dispense_count !== 1 ? "s" : ""}`
    );
  }
  if (patient.reminder_count > 0) {
    parts.push(
      `${patient.reminder_count} reminder${patient.reminder_count !== 1 ? "s" : ""}`
    );
  }
  return parts.length > 0 ? parts.join(", ") : null;
}

export interface PatientComboboxProps {
  value?: string;
  onSelect?: (patient: Patient | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  displayLabel?: string;
  onManageClick?: () => void;
  /** Called when the user wants to create a new patient from the combobox. Receives the typed search text. */
  onCreateClick?: (searchText: string) => void;
  refreshTrigger?: number;
}

export function PatientCombobox({
  value,
  onSelect,
  placeholder = "Select patient...",
  searchPlaceholder = "Search patients...",
  disabled = false,
  className,
  displayLabel,
  onManageClick,
  onCreateClick,
  refreshTrigger,
}: PatientComboboxProps) {
  const { toast } = useToast();
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [patients, setPatients] = React.useState<Patient[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [selectedPatient, setSelectedPatient] = React.useState<Patient | null>(
    null
  );

  // Load patients when component mounts or popover opens
  const loadPatients = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getPatients();
      setPatients(data.data.patients || []);
    } catch (error) {
      console.error("Load error:", error);
      toast({
        title: "Error",
        description:
          error instanceof ApiClientError
            ? error.message
            : "Failed to load patients. Please try again.",
        variant: "destructive",
      });
      setPatients([]);
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Load patients on mount and when refreshTrigger changes
  React.useEffect(() => {
    loadPatients();
  }, [loadPatients, refreshTrigger]);

  // Set selected patient based on value prop
  React.useEffect(() => {
    if (value && patients.length > 0) {
      const found = patients.find((p) => p.id === value);
      if (found) {
        setSelectedPatient(found);
      }
    } else if (!value) {
      setSelectedPatient(null);
    }
  }, [value, patients]);

  // Filter patients based on search query
  const filteredPatients = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return patients;
    }

    const query = searchQuery.toLowerCase();
    return patients.filter((p) => {
      const fullName = formatPatientName(p).toLowerCase();
      return fullName.includes(query);
    });
  }, [patients, searchQuery]);

  // Check if search query is a new patient name
  const isNewPatient =
    searchQuery.trim().length > 0 &&
    !patients.some(
      (p) =>
        formatPatientName(p).toLowerCase() === searchQuery.trim().toLowerCase()
    );

  const handleSelect = (patient: Patient) => {
    setSelectedPatient(patient);
    onSelect?.(patient);
    setSearchQuery("");
    setOpen(false);
  };

  const handleCreateNew = () => {
    if (!searchQuery.trim()) return;
    setOpen(false);
    onCreateClick?.(searchQuery.trim());
    setSearchQuery("");
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSearchQuery("");
    }
  };

  const handleManageClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    onManageClick?.();
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
          disabled={disabled}
        >
          <span className="truncate">
            {displayLabel ||
              (selectedPatient
                ? formatPatientName(selectedPatient)
                : placeholder)}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-full min-w-[300px] max-w-[500px] p-0"
        align="start"
      >
        <div className="flex flex-col">
          {/* Search Input */}
          <div className="flex items-center border-b border-border px-3">
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && isNewPatient && onCreateClick) {
                  e.preventDefault();
                  handleCreateNew();
                }
              }}
              className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
              autoFocus
            />
          </div>

          {/* Results List */}
          <div className="max-h-[300px] overflow-y-auto overflow-x-hidden">
            {isLoading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="ml-2 text-sm text-muted-foreground">
                  Loading...
                </span>
              </div>
            )}

            {!isLoading && (
              <>
                {/* Create New Option */}
                {isNewPatient && onCreateClick && (
                  <div className="border-b border-border p-1">
                    <button
                      type="button"
                      onClick={handleCreateNew}
                      className="relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none transition-colors hover:bg-primary-soft hover:text-primary focus:bg-primary-soft focus:text-primary"
                    >
                      <Plus className="mr-2 h-4 w-4 shrink-0" />
                      <div className="flex flex-col items-start">
                        <span className="font-medium">
                          Create &quot;{searchQuery.trim()}&quot;
                        </span>
                        <span className="text-xs text-muted-foreground">
                          Add new patient
                        </span>
                      </div>
                    </button>
                  </div>
                )}

                {/* Existing Patients */}
                {filteredPatients.length > 0 && (
                  <div className="p-1">
                    {filteredPatients.map((patient) => {
                      const usageSummary = formatUsageSummary(patient);
                      return (
                        <button
                          key={patient.id}
                          type="button"
                          onClick={() => handleSelect(patient)}
                          className={cn(
                            "relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none transition-colors hover:bg-primary-soft hover:text-primary focus:bg-primary-soft focus:text-primary",
                            selectedPatient?.id === patient.id &&
                              "bg-primary-soft text-primary"
                          )}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4 shrink-0",
                              selectedPatient?.id === patient.id
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                          <div className="flex flex-col items-start flex-1">
                            <span className="font-medium">
                              {formatPatientName(patient)}
                            </span>
                            {usageSummary && (
                              <span className="text-xs text-muted-foreground">
                                {usageSummary}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Empty State */}
                {!isNewPatient &&
                  filteredPatients.length === 0 &&
                  searchQuery.trim().length > 0 && (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      No patients found
                    </div>
                  )}

                {!isNewPatient &&
                  filteredPatients.length === 0 &&
                  searchQuery.trim().length === 0 &&
                  patients.length === 0 && (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      No patients yet.
                      {onCreateClick && " Type a name to create one."}
                    </div>
                  )}
              </>
            )}
          </div>

          {/* Manage Button */}
          {onManageClick && !isLoading && (
            <div className="border-t border-border p-1">
              <button
                type="button"
                onClick={handleManageClick}
                className="relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none transition-colors hover:bg-primary-soft hover:text-primary focus:bg-primary-soft focus:text-primary"
              >
                <Settings className="mr-2 h-4 w-4 shrink-0" />
                <span>Manage Patients</span>
              </button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
